import PersonChip from "@/estimating/components/PersonChip";
import { findPerson, shortName, usePeople } from "@/lib/people";

/** AM / Est chip that opens a picker of the people flagged for that role (Settings › Sales).
 *  kind: "am" | "est" | "pm" | "pe" | "foreman"; stores initials (Pipeline, Contracts, Reconcile) or email (valueKey). */
const ROLE_OF = { pm: "pm", pe: "project_engineer", foreman: "foreman" };
const NOUN = { am: "account managers", est: "estimators", pm: "project managers", pe: "project engineers", foreman: "foremen" };
export const peopleFor = (people, kind) => people.filter((p) => p.active !== false).filter((p) => (kind === "am" ? p.is_am : kind === "est" ? p.is_est : p.role === ROLE_OF[kind]));

export default function PersonPick({ value, kind, onChange, readOnly = false, testid, valueKey = "initials", asName = false, strict = false }) {
  const { people } = usePeople();
  const list = peopleFor(people, kind);
  const current = findPerson(people, value);
  const chip = value ? (asName
      ? <span className="whitespace-nowrap text-xs text-slate-700" data-testid={`${testid}-chip`}>{shortName(current?.name || value)}</span>
      : <PersonChip name={value} testid={`${testid}-chip`} />)
    : <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-slate-300 text-[10px] text-slate-400">?</span>;
  if (readOnly) return <span data-testid={testid}>{value ? chip : <span className="text-slate-400">--</span>}</span>;
  // Same control as the Bid Board: the chip / name with a native dropdown of Settings › Users over it.
  // People holding the role come first; everyone else active in Settings › Users follows.
  // PM / PE / Foreman are strict to their Settings › Users role; AM / Est also list everyone else.
  const others = ROLE_OF[kind] || strict ? [] : people.filter((p) => p.active !== false && !list.includes(p));
  const label = (p) => p.name;
  return (
    <label className={`relative inline-flex cursor-pointer ${asName ? "rounded px-1 -mx-1 hover:bg-slate-100" : "rounded-full transition-transform hover:scale-110"}`}
           title={current?.name || value || "Assign"} onClick={(e) => e.stopPropagation()}>
      {chip}
      <select value={current ? current[valueKey] : value || ""} onChange={(e) => e.target.value !== (value || "") && onChange(e.target.value)}
              data-testid={testid} className="absolute inset-0 cursor-pointer opacity-0">
        <option value="">Unassigned</option>
        {list.length === 0 && <option disabled value="__none">No active {NOUN[kind]} in Settings › Users</option>}
        {list.length > 0 && <optgroup label={NOUN[kind].replace(/^\w/, (c) => c.toUpperCase())}>
          {list.map((p) => <option key={p.email} value={p[valueKey]}>{label(p)}</option>)}</optgroup>}
        {others.length > 0 && <optgroup label={list.length ? "Everyone else" : "Settings › Users"}>
          {others.map((p) => <option key={p.email} value={p[valueKey]}>{label(p)}</option>)}</optgroup>}
        {value && !current && <option value={value}>{value}</option>}
      </select>
    </label>
  );
}
